import React from 'react'
import { snakeCase } from 'lodash'
import moment from 'moment'
import { Badge, Button, Col, Form, Row } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit'
import { Checkbox, OverlayLink, getColumnString } from '../../common'
import { modifyArticle } from '../articleAPI'
import { openModal, selectCell, selectId, selectTargetArticle, unselectId } from '../articleActions'
import { getRowNumberColumn } from '../common'
import {
  coerceArticleTypeOnPriorityChange,
  isAreaArticleType,
  isDisabledArticleType,
  isRelationTypeSameArticle,
  isSizedArticleType,
  isTopPositionPriority
} from '../ArticleParse'
import { DataListColumn } from '../../common'
import { figureSchema, isValidArea } from '../validateLayoutable'
import { getSwitchUpTargetIdx, getSwitchDownTargetIdx } from './articleListFunctions'
import { getSubmitParamForArticleUpsert, layoutMissed } from './utils'
import { SizedArticleArea } from './SizedArticleArea'
import type { Article } from '../types'
import type { LayoutableWithMeta } from './helpers'
import type { UpsertParams } from './utils'
import type { ArticleTypeValue, PriorityValue } from '../../common/types'
import { ArticleType, Priority } from '../../common/types'

type Dispatch = ThunkDispatch<unknown, unknown, UnknownAction>

export const buildCheckboxColumn = ({
  selectedIds,
  dispatch
}: {
  selectedIds: number[]
  dispatch: Dispatch
}): DataListColumn => ({
  id: 'checkboxColumn',
  meta: { cellProps: { style: { width: '40px', textAlign: 'center' } } },
  cell: (props) => (
    <Checkbox
      value={props.row.original.id}
      checked={selectedIds.includes(parseInt(props.row.original.id))}
      onChange={(value: string, checked: boolean) => {
        dispatch((checked ? selectId : unselectId)(parseInt(value)))
      }}
    />
  )
})

export const buildRowNumberColumn = (): DataListColumn => ({
  id: 'rowNumberColumn',
  name: '#',
  cell: (props) => {
    const row = props.row.original
    const rowNumber = props.row.index + 1
    return getRowNumberColumn(row, rowNumber)
  }
})

export const buildActionColumn = ({
  dispatch,
  isSelectedLayoutableWritable,
  parentArticleIndices,
  isSwitchUpDisabled,
  isSwitchDownDisabled,
  switchArticleOrder,
  nonHolderArticles
}: {
  dispatch: Dispatch
  isSelectedLayoutableWritable: boolean
  parentArticleIndices: number[]
  isSwitchUpDisabled: (article: Article, rowIdx: number) => boolean
  isSwitchDownDisabled: (article: Article, rowIdx: number) => boolean
  switchArticleOrder: (articles: Article[], fromIdx: number, toIdx: number) => void
  nonHolderArticles: Article[]
}): DataListColumn => ({
  id: 'actionColumn',
  name: '編集',
  meta: { cellProps: { style: { width: '70px' } } },
  cell: (props) => {
    const article: Article = props.row.original
    const articleId = parseInt(article.id as string)
    const keyBase = `article-${articleId}-action`
    const inputDisabled = !isSelectedLayoutableWritable || article.layoutLocked
    if (inputDisabled) {
      return (
        <>
          <OverlayLink
            keyName={`${keyBase}-view`}
            tooltipText='記事内容詳細'
            placement='auto'
            icon='info-circle'
            disabled={!inputDisabled}
            onClick={(e: React.MouseEvent) => {
              e.preventDefault()
              dispatch(selectTargetArticle(article as any))
              dispatch(openModal('view'))
            }}
          />
          {article.layoutLocked && (
            <OverlayLink
              keyName={`${keyBase}-unlock`}
              tooltipText='レイアウトロック解除'
              placement='auto'
              icon='unlock'
              disabled={!isSelectedLayoutableWritable}
              onClick={(e: React.MouseEvent) => {
                e.preventDefault()
                dispatch(selectTargetArticle(article as any))
                dispatch(openModal('unlock'))
              }}
            />
          )}
        </>
      )
    }
    return (
      <span>
        <OverlayLink
          keyName={`${keyBase}-edit`}
          tooltipText='記事内容編集'
          placement='auto'
          icon='edit'
          disabled={inputDisabled}
          onClick={(e: React.MouseEvent) => {
            e.preventDefault()
            dispatch(selectTargetArticle(article as any))
            dispatch(openModal('edit'))
          }}
        />
        <OverlayLink
          keyName={`${keyBase}-up`}
          placement='auto'
          tooltipText='優先順位を上げる'
          icon='angle-double-up'
          disabled={isSwitchUpDisabled(article, props.row.index)}
          onClick={(e: React.MouseEvent) => {
            e.preventDefault()
            const toIdx = getSwitchUpTargetIdx(article, props.row.index, parentArticleIndices)
            switchArticleOrder(nonHolderArticles, props.row.index, toIdx!)
          }}
        />
        <OverlayLink
          keyName={`${keyBase}-down`}
          placement='auto'
          tooltipText='優先順位を下げる'
          disabled={isSwitchDownDisabled(article, props.row.index)}
          onClick={(e: React.MouseEvent) => {
            e.preventDefault()
            const toIdx = getSwitchDownTargetIdx(article, props.row.index, parentArticleIndices)
            switchArticleOrder(nonHolderArticles, props.row.index, toIdx!)
          }}
          icon='angle-double-down'
        />
      </span>
    )
  }
})

export const buildArticleTypeColumn = ({
  dispatch,
  layoutableId,
  layoutableType,
  layoutable,
  enumValues,
  fetching,
  isSelectedLayoutableWritable
}: {
  dispatch: Dispatch
  layoutableId: number | string
  layoutableType: string
  layoutable: LayoutableWithMeta | null | undefined
  enumValues: any
  fetching: boolean
  isSelectedLayoutableWritable: boolean
}): DataListColumn => ({
  id: 'articleType',
  name: '記事種別',
  cell: (props) => {
    const article: Article = props.row.original
    return (
      <Form.Select
        name='articleType'
        value={article.articleType}
        onChange={(e) => {
          const articleType = e.target.value as ArticleTypeValue
          const newArticle: Article = { ...article, articleType }
          if (articleType === ArticleType.Flow) {
            newArticle.areas = undefined
          } else if (isSizedArticleType(articleType)) {
            if (isTopPositionPriority(newArticle.priority as PriorityValue)) {
              newArticle.areas = [{ x: null, y: 0, width: null, height: null, fromLeft: false }]
            } else {
              newArticle.areas = undefined
            }
          }
          const submitProps: UpsertParams = {
            layoutableId,
            layoutableType,
            layoutableLockVersion: layoutable?.lockVersion,
            article: newArticle,
            loadedBinaryFile: undefined,
            deleteFile: undefined,
            auxiliaryLine: null,
            successAlertBody: undefined
          }
          dispatch(modifyArticle(getSubmitParamForArticleUpsert(submitProps) as any))
        }}
        size='sm'
        disabled={!isSelectedLayoutableWritable || isAreaArticleType(article.articleType) || article.layoutLocked}
      >
        {Object.keys(enumValues.articleTypes).map((articleType) => (
          <option
            key={articleType}
            value={snakeCase(articleType)}
            disabled={
              fetching ||
              isAreaArticleType(articleType as ArticleTypeValue) ||
              isDisabledArticleType(article.priority as PriorityValue, articleType as ArticleTypeValue)
            }
          >
            {enumValues.articleTypes[articleType]}
          </option>
        ))}
      </Form.Select>
    )
  }
})

export const buildPriorityColumn = ({
  dispatch,
  layoutableId,
  layoutableType,
  layoutable,
  enumValues,
  isSelectedLayoutableWritable,
  largestOrder,
  usedPriorities
}: {
  dispatch: Dispatch
  layoutableId: number | string
  layoutableType: string
  layoutable: LayoutableWithMeta | null | undefined
  enumValues: any
  isSelectedLayoutableWritable: boolean
  largestOrder: number
  usedPriorities: Set<string>
}): DataListColumn => ({
  id: 'priorityColumn',
  name: '記事優先度',
  cell: (props) => {
    const article: Article = props.row.original
    return (
      <Form.Select
        name='priority'
        value={article.priority ?? ''}
        onChange={(e) => {
          const priority = e.target.value as PriorityValue
          const articleType = coerceArticleTypeOnPriorityChange(priority, article.articleType)
          const order = priority === Priority.Other ? largestOrder + 1 : 0
          const newArticle: Article = { ...article, articleType, priority, order }
          if (isSizedArticleType(newArticle.articleType)) {
            if (isTopPositionPriority(priority)) {
              newArticle.areas = [{ x: null, y: 0, width: null, height: null, fromLeft: false }]
            } else {
              newArticle.areas = undefined
            }
          }
          const submitProps: UpsertParams = {
            layoutableId,
            layoutableType,
            layoutableLockVersion: layoutable?.lockVersion,
            article: newArticle,
            loadedBinaryFile: undefined,
            deleteFile: undefined,
            auxiliaryLine: null,
            successAlertBody: undefined
          }
          dispatch(modifyArticle(getSubmitParamForArticleUpsert(submitProps) as any))
        }}
        size='sm'
        disabled={!isSelectedLayoutableWritable || article.articleType === ArticleType.Template || article.layoutLocked}
      >
        {Object.keys(enumValues.priorities).map((k) => (
          <option key={k} value={snakeCase(k)} disabled={usedPriorities.has(k)}>
            {enumValues.priorities[k]}
          </option>
        ))}
      </Form.Select>
    )
  }
})

export const buildNameColumn = ({
  dispatch,
  missedArticles
}: {
  dispatch: Dispatch
  missedArticles: Set<string>
}): DataListColumn => ({
  id: 'name',
  name: '名称',
  cell: (props) => {
    const article: Article = props.row.original
    const name = article.filename || article.name
    const articleLayoutLockedIcon = article.layoutLocked ? (
      <>
        <FontAwesomeIcon icon='lock' />{' '}
      </>
    ) : undefined
    const nameColumn = layoutMissed(props, missedArticles) ? (
      <Col className='text-danger'>
        <FontAwesomeIcon icon='triangle-exclamation' />
        {name}
      </Col>
    ) : (
      <Col>
        {articleLayoutLockedIcon}
        {name}
      </Col>
    )
    const unassignedFigureCount =
      article?.templateArticles?.filter((t) => t.templateArticleType === 'figure' && !t?.location)?.length || 0
    const templateArticle =
      article.articleType === ArticleType.Template ? (
        <Col sm='auto'>
          <Button
            size='sm'
            variant='outline-primary'
            onClick={(e) => {
              e.preventDefault()
              dispatch(
                selectCell({
                  articleId: article.id as unknown as number,
                  rowId: props.row.index,
                  column: 'templateArticle'
                })
              )
            }}
          >
            在版記事
            {unassignedFigureCount > 0 && (
              <>
                &nbsp;
                <Badge pill bg='danger'>
                  {unassignedFigureCount}
                </Badge>
              </>
            )}
          </Button>
        </Col>
      ) : (
        <></>
      )
    if (article?.auxiliaryLine !== null) {
      const al = article.auxiliaryLine
      const auxiliaryLineStr = `補助線あり(${(al?.startColumn ?? 0) + 1}段目${(al?.startRow ?? 0) + 1}行目${al?.length}段)`
      return (
        <Row>
          {nameColumn}
          <Col sm='auto'>
            <Badge bg='secondary'>{auxiliaryLineStr}</Badge>
          </Col>
        </Row>
      )
    }
    return (
      <Row>
        {nameColumn}
        {templateArticle}
      </Row>
    )
  }
})

export const buildHeadingsColumn = (): DataListColumn => ({
  id: 'headings',
  name: '見出し',
  meta: { cellProps: { style: { cursor: 'pointer' } } },
  cell: (props) => {
    const article: Article = props.row.original
    return article.headings?.length
      ? `${article.headingNumberOfOccupyingColumns}段${article.headings.length}本${article.headingNumberOfOccupyingRows}行取り`
      : ''
  }
})

export const buildPreambleColumn = (): DataListColumn => ({
  id: 'preambleColumn',
  name: '前文',
  meta: { cellProps: { style: { cursor: 'pointer' } } },
  cell: (props) => {
    const preambleNumberOfRows = props.row.original.preambleNumberOfRows
    return preambleNumberOfRows ? `${preambleNumberOfRows}行` : ''
  }
})

export const buildBodyColumn = (): DataListColumn => ({
  name: '本文',
  id: 'bodyColumn',
  meta: { cellProps: { style: { cursor: 'pointer' } } },
  cell: (props) => {
    const article: Article = props.row.original
    const columnSpaceExistence = article.columnSpaceExistence ? '中段有' : '中段無'
    return article.bodyNumberOfRows ? `${article.bodyNumberOfRows}行/${columnSpaceExistence}` : ''
  }
})

export const buildFiguresColumn = (): DataListColumn => ({
  id: 'figures',
  name: '写真/図',
  meta: { cellProps: { style: { cursor: 'pointer' } } },
  cell: (props) => {
    const figures = props.row.original.figures
    const figuresCount = figures?.length || ''
    const invalidFiguresCount = figures?.filter((fig: any) => !figureSchema.isValidSync(fig))?.length || 0
    return (
      <>
        {figuresCount}&nbsp;
        {invalidFiguresCount > 0 && (
          <Badge pill bg='danger'>
            {invalidFiguresCount}
          </Badge>
        )}
      </>
    )
  }
})

export const buildAreasColumn = ({
  layoutable,
  childrenArticles
}: {
  layoutable: LayoutableWithMeta | null | undefined
  childrenArticles: Record<string, Article[]>
}): DataListColumn => ({
  id: 'areas',
  name: '領域',
  meta: { cellProps: { style: { cursor: 'pointer' } } },
  cell: (props) => {
    const article: Article = props.row.original
    if (
      article.articleType === ArticleType.Flow ||
      isSizedArticleType(article.articleType) ||
      isRelationTypeSameArticle(childrenArticles, article)
    ) {
      return <SizedArticleArea article={article} />
    } else if (!article?.areas?.length) {
      return ''
    }
    const { width, height } = article.areas[0]
    const columnString = getColumnString(height != null ? String(height) : height)
    const invalidAreasCount = article.areas?.filter((area) => !isValidArea(layoutable!, area))?.length || 0
    return (
      <>
        {`天地${columnString} 左右${width != null ? parseInt(String(width)) : ''}行幅`}
        {invalidAreasCount > 0 && (
          <>
            &nbsp;
            <Badge pill bg='danger'>
              !
            </Badge>
          </>
        )}
      </>
    )
  }
})

export const buildUpdatedAtColumn = (): DataListColumn => ({
  id: 'updatedAt',
  name: '更新日時',
  cell: (props) => moment(props.row.original.updatedAt as string).format('MMMDo LT')
})
